import { useState, useEffect } from "react";
import {
    Container,
    Table,
    NumberInput,
    Slider,
    Text,
    Card,
    Grid,
    TextInput,
} from "@mantine/core";

const sectorsList = [
    "Transport",
    "Industries",
    "Cooking",
    "Heating",
    "Gen. Sets",
    "Road dust",
    "Waste burn",
    "Brick kilns",
    "Power plants",
    "Others",
];

const Home = () => {
    // 🔥 TOTAL PM VALUE (INPUT)
    const [totalPM, setTotalPM] = useState(85);

    // 🔥 10 SECTORS
    const [sectorslist, setSectorsList] = useState(sectorsList);
    const [sectors, setSectors] = useState(
        sectorsList.map(() => ({
            share: 10, // %
            reduction: 0,
        }))
    );

    const [result, setResult] = useState({
        oldTotal: 0,
        newTotal: 0,
        netReduction: 0,
    });

    // 🔥 UPDATE INPUT
    const updateSector = (index, key, value) => {
        const updated = [...sectors];
        updated[index][key] =
            value === "" || value === null ? 0 : Number(value);
        setSectors(updated);
    };

    // 🔥 CALCULATIONS
    useEffect(() => {
        const oldTotal = Number(totalPM || 0);

        // Step 1: Calculate sector concentrations
        const sectorData = sectors.map((s) => {
            const share = Number(s.share || 0);
            const reduction = Number(s.reduction || 0);

            const conc = (oldTotal * share) / 100;
            const newConc = conc - (conc * reduction) / 100;

            return {
                conc,
                newConc,
            };
        });

        // Step 2: New total
        const newTotal = sectorData.reduce(
            (sum, s) => sum + s.newConc,
            0
        );

        // Step 3: Net reduction
        const netReduction =
            oldTotal === 0 ? 0 : ((oldTotal - newTotal) / oldTotal) * 100;

        setResult({
            oldTotal,
            newTotal,
            netReduction,
        });
    }, [sectors, totalPM]);

    return (
        <Container size="xl" py="md">
            <Text size="xl" fw={700} ta="center" mb="md">
                -- AQ Simulator V2 --
            </Text>

            {/* 🔥 TOTAL INPUT */}
            <Card mb="md" p="md" withBorder radius="lg" style={{ background: "#e6f4ea" }}>
                <Text size="sm" c="dimmed" mb="xs">
                    Green cells are inputs. Use sliders to adjust reduction. Scroll down for notes on how to use this simulator.
                </Text>
                <Text size="sm" c="black">
                    Average Airshed Concentration (µg/m³)
                </Text>
                <NumberInput
                    value={totalPM}
                    onChange={(val) =>
                        setTotalPM(val === "" || val === null ? 0 : Number(val))
                    }
                    min={0}
                    hideControls
                    styles={{
                        input: {
                            // textAlign: "center",
                            fontSize: "16px",
                            background: "transparent",
                            // border: "none",
                        },
                    }}
                />
            </Card>

            {/* 🔥 TABLE */}
            <Card shadow="md" p="md" radius="lg" withBorder>

                <div style={{ overflowX: "auto" }}>
                    <Table
                        striped
                        withColumnBorders
                        highlightOnHover
                        fontSize="md"
                        styles={{
                            th: { textAlign: "center" },
                            td: { textAlign: "center" },
                        }}
                    >
                        <thead>
                            <tr>
                                <th style={{ width: 80, fontSize: "16px" }}>Sector</th>
                                <th style={{ width: 80, fontSize: "16px" }}>Share (%)</th>
                                <th style={{ width: 80, fontSize: "16px" }}>Avg Conc (µg/m³)</th>
                                <th style={{ width: 80, fontSize: "16px" }}>% Reduction</th>
                                <th style={{ width: 80, fontSize: "16px" }}>New Conc</th>
                                <th style={{ width: 80, fontSize: "16px" }}>New Share (%)</th>
                            </tr>
                        </thead>

                        <tbody>
                            {sectors.map((s, index) => {
                                const share = Number(s.share || 0);
                                const reduction = Number(s.reduction || 0);

                                const conc = (totalPM * share) / 100;
                                const newConc = conc - (conc * reduction) / 100;

                                const newShare =
                                    result.newTotal === 0
                                        ? 0
                                        : (newConc / result.newTotal) * 100;

                                return (
                                    <tr key={index}>
                                        <td style={{ background: "wheat", }}>
                                            <TextInput
                                                value={sectorslist[index]}
                                                onChange={(e) => {
                                                    const updated = [...sectorslist];
                                                    updated[index] = e.target.value;
                                                    setSectorsList(updated);
                                                }}
                                                styles={{
                                                    input: {
                                                        textAlign: "center",
                                                        fontSize: "16px",
                                                        background: "transparent",
                                                        // border: "none",
                                                    },
                                                }}
                                            />
                                        </td>

                                        {/* SHARE */}
                                        <td style={{ background: "#e6f4ea" }}>
                                            <NumberInput
                                                value={share}
                                                min={0}
                                                max={100}
                                                hideControls
                                                onChange={(val) =>
                                                    updateSector(index, "share", val)
                                                }
                                                styles={{
                                                    input: {
                                                        textAlign: "center",
                                                        fontSize: "16px",
                                                        background: "transparent",
                                                        // border: "none",
                                                    },
                                                }}
                                            />
                                        </td>

                                        {/* AVG CONC */}
                                        <td style={{ fontWeight: 500, textAlign: 'center' }}>{conc.toFixed(1)}</td>

                                        {/* SLIDER */}
                                        <td>
                                            <div style={{ display: "flex", gap: 10 }}>
                                                <Slider
                                                    value={reduction}
                                                    onChange={(val) =>
                                                        updateSector(index, "reduction", val)
                                                    }
                                                    min={0}
                                                    max={99}
                                                    style={{ flex: 1 }}
                                                    m={'sm'}
                                                />
                                                <Text w={40} size="sm">{reduction}%</Text>
                                            </div>
                                        </td>

                                        {/* NEW CONC */}
                                        <td style={{ fontWeight: 500, textAlign: 'center' }}>{newConc.toFixed(1)}</td>

                                        {/* NEW SHARE */}
                                        <td style={{ fontWeight: 500, textAlign: 'center' }}>{newShare.toFixed(1)}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {/* 🔥 RESULTS */}
            <Card mt="md" p="lg" withBorder radius="lg">
                <Grid>
                    <Grid.Col span={4}>
                        <Text size="sm">Old Concentration</Text>
                        <Text fw={700} size="xl">
                            {result.oldTotal.toFixed(1)} µg/m³
                        </Text>
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <Text size="sm">New Concentration</Text>
                        <Text fw={700} size="xl" c="teal">
                            {result.newTotal.toFixed(1)} µg/m³
                        </Text>
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <Text size="sm">Net Reduction</Text>
                        <Text
                            fw={700}
                            size="xl"
                            c={result.netReduction > 0 ? "green" : "red"}
                        >
                            {result.netReduction.toFixed(1)}%
                        </Text>
                    </Grid.Col>
                </Grid>
            </Card>

            {/* 🔥 VISUALIZATION SECTION */}

            <Card mt="md" p="lg" radius="lg" withBorder>
                <Text fw={700} size="lg" mb="lg" ta="center">
                    Visualization
                </Text>

                {/* ===================== */}
                {/* 🔵 ABSOLUTE CONCENTRATION */}
                {/* ===================== */}

                <Text fw={600} mb="sm">
                    Absolute Concentrations (µg/m³)
                    <Text span size="xs" c="dimmed">
                        (hover for numbers)
                    </Text>
                </Text>


                {/* BEFORE (FULL WIDTH ALWAYS) */}
                <div style={{ marginBottom: 20 }}>
                    <Text size="sm" mb="xs" c="dimmed">
                        Before
                    </Text>

                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                flex: 1,
                                height: 28,
                                display: "flex",
                                borderRadius: 6,
                                overflow: "hidden",
                                boxShadow: "inset 0 0 4px rgba(0,0,0,0.1)",
                            }}
                        >
                            {sectors.map((s, i) => {
                                const width = Number(s.share || 0);

                                return (
                                    <div
                                        key={i}
                                        title={sectorslist[i]}
                                        style={{
                                            width: `${width}%`,
                                            background: `hsl(${i * 35},70%,60%)`,
                                            borderRight: "2px solid #fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#000",
                                            fontWeight: 700,
                                            fontSize: 11,
                                        }}
                                    >
                                        {width > 6 &&
                                            sectorslist[i].slice(0, 2).toUpperCase()}
                                    </div>
                                );
                            })}
                        </div>

                        {/* VALUE BOX */}
                        <div
                            style={{
                                width: 80,
                                textAlign: "center",
                                background: "#f1f3f5",
                                padding: "4px 8px",
                                borderRadius: 6,
                                fontWeight: 600,
                            }}
                        >
                            {result.oldTotal.toFixed(1)}
                        </div>
                    </div>
                </div>

                {/* AFTER (SCALED RELATIVE TO BEFORE) */}
                <div style={{ marginBottom: 30 }}>
                    <Text size="sm" mb="xs" c="dimmed">
                        After
                    </Text>

                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {/* OUTER FULL BAR */}
                        <div
                            style={{
                                flex: 1,
                                height: 28,
                                position: "relative",
                                background: "#f1f3f5",
                                borderRadius: 6,
                                overflow: "hidden",
                                boxShadow: "inset 0 0 4px rgba(0,0,0,0.1)",
                            }}
                        >
                            {/* INNER SCALED BAR */}
                            <div
                                style={{
                                    display: "flex",
                                    height: "100%",
                                    width:
                                        result.oldTotal === 0
                                            ? "0%"
                                            : `${(result.newTotal / result.oldTotal) * 100}%`,
                                    transition: "width 0.4s ease",
                                }}
                            >
                                {sectors.map((s, i) => {
                                    const share = Number(s.share || 0);
                                    const reduction = Number(s.reduction || 0);

                                    const conc = (totalPM * share) / 100;
                                    const newConc = conc - (conc * reduction) / 100;

                                    const newShare =
                                        result.newTotal === 0
                                            ? 0
                                            : (newConc / result.newTotal) * 100;

                                    return (
                                        <div
                                            key={i}
                                            title={`${sectorslist[i]}: ${newShare.toFixed(
                                                1
                                            )}%`}
                                            style={{
                                                width: `${newShare}%`,
                                                background: `hsl(${i * 35},70%,50%)`,
                                                borderRight: "2px solid #fff",
                                                display: "flex",
                                                // alignItems: "center",
                                                // justifyContent: "center",
                                                color: "#fff",
                                                fontSize: 11,
                                                fontWeight: 600,
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {/* SMART LABEL */}

                                            {/* <Text size="sm">{newShare.toFixed(0)}%</Text> */}
                                            {/* {newShare > 12 ? (
                                                <span>
                                                    {sectorslist[i]
                                                        .slice(0, 2)
                                                        .toUpperCase()}{" "}
                                                    {newShare.toFixed(0)}%
                                                </span>
                                            ) : newShare > 6 ? (
                                                <span>{newShare.toFixed(0)}%</span>
                                            ) : null} */}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* VALUE BOX */}
                        <div
                            style={{
                                width: 80,
                                textAlign: "center",
                                background: "#e6fcf5",
                                padding: "4px 8px",
                                borderRadius: 6,
                                fontWeight: 700,
                            }}
                        >
                            {result.newTotal.toFixed(1)}
                        </div>
                    </div>
                </div>

                {/* ===================== */}
                {/* 🟢 SOURCE CONTRIBUTION */}
                {/* ===================== */}

                <Text fw={600} mb="sm">
                    Source Contributions (out of 100%){" "}
                    <Text span size="xs" c="dimmed">
                        (hover for numbers)
                    </Text>
                </Text>

                {/* BEFORE SHARE */}
                <div style={{ marginBottom: 20 }}>
                    <Text size="sm" mb="xs" c="dimmed">
                        Before
                    </Text>

                    <div style={{ display: "flex", height: 24 }}>
                        {sectors.map((s, i) => {
                            const width = Number(s.share || 0);

                            // ✅ fallback if user clears input
                            const name =
                                sectorslist[i] && sectorslist[i].trim() !== ""
                                    ? sectorslist[i]
                                    : sectorsList[i];

                            return (
                                <div
                                    key={i}
                                    title={`${name}: ${width.toFixed(1)}%`}
                                    style={{
                                        width: `${width}%`,
                                        background: `hsl(${i * 35},70%,60%)`,
                                        borderRight: "2px solid #fff",
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>

                {/* AFTER SHARE */}
                <div>
                    <Text size="sm" mb="xs" c="dimmed">
                        After
                    </Text>

                    <div style={{ display: "flex", height: 24 }}>
                        {sectors.map((s, i) => {
                            const share = Number(s.share || 0);
                            const reduction = Number(s.reduction || 0);

                            const conc = (totalPM * share) / 100;
                            const newConc = conc - (conc * reduction) / 100;

                            const newShare =
                                result.newTotal === 0
                                    ? 0
                                    : (newConc / result.newTotal) * 100;

                            // ✅ fallback name
                            const name =
                                sectorslist[i] && sectorslist[i].trim() !== ""
                                    ? sectorslist[i]
                                    : sectorsList[i];

                            return (
                                <div
                                    key={i}
                                    title={`${name}: ${newShare.toFixed(1)}%`}
                                    style={{
                                        width: `${newShare}%`,
                                        background: `hsl(${i * 35},70%,50%)`,
                                        borderRight: "2px solid #fff",
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>

                {/* <Text fw={600} mb="sm">
                    Source Contribution (%)
                </Text> */}

                {/* BEFORE SHARE */}
                {/* <div style={{ marginBottom: 20 }}>
                    <Text size="sm" mb="xs" c="dimmed">
                        Before
                    </Text>

                    <div style={{ display: "flex", height: 24 }}>
                        {sectors.map((s, i) => {
                            const width = Number(s.share || 0);

                            return (
                                <div
                                    key={i}
                                    title={`${sectorslist[i]}: ${width.toFixed(1)}%`}
                                    style={{
                                        width: `${width}%`,
                                        background: `hsl(${i * 35},70%,60%)`,
                                        borderRight: "2px solid #fff",
                                    }}
                                    
                                />
                            );
                        })}
                    </div>
                </div> */}

                {/* AFTER SHARE */}
                {/* <div>
                    <Text size="sm" mb="xs" c="dimmed">
                        After
                    </Text>

                    <div style={{ display: "flex", height: 24 }}>
                        {sectors.map((s, i) => {
                            const share = Number(s.share || 0);
                            const reduction = Number(s.reduction || 0);

                            const conc = (totalPM * share) / 100;
                            const newConc = conc - (conc * reduction) / 100;

                            const newShare =
                                result.newTotal === 0
                                    ? 0
                                    : (newConc / result.newTotal) * 100;

                            return (
                                <div
                                    key={i}
                                    title={`${sectorslist[i]}: ${newShare.toFixed(
                                        1
                                    )}%`}
                                    style={{
                                        width: `${newShare}%`,
                                        background: `hsl(${i * 35},70%,50%)`,
                                        borderRight: "2px solid #fff",
                                    }}
                                />
                            );
                        })}
                    </div>
                </div> */}
            </Card>
        </Container>
    );
};

export default Home;